import { CommandDialog } from '@/components/ui/command';
import { EntitySearchContent } from '@/features/shared/components/form-items-with-fetch/entity-search-combobox';
import { useSearchEntityNavigation, type SearchEntityItem } from '@/features/shared/hooks/use-search-entity';
import type { GlobalSearchTab } from '@/server/types/search-types';
import { SearchEntity } from '@/server/types/search-types';
import * as React from 'react';

type GlobalSearchContextType = {
    openSearch: (defaultTab?: GlobalSearchTab) => void;
    open: boolean;
};

const GlobalSearchContext = React.createContext<GlobalSearchContextType | undefined>(undefined);

export type Props = {
    onBeforeOpenSearch: () => void;
    needsToCloseOtherWindowBeforeOpeningSearch?: boolean;
} & React.PropsWithChildren;

export const useGlobalSearch = (): GlobalSearchContextType => {
    const context = React.useContext(GlobalSearchContext);
    if (!context) {
        throw new Error('useGlobalSearch must be used within a GlobalSearchProvider');
    }
    return context;
};

export const GlobalSearchBarProvider: React.FC<Props> = ({
    children,
    onBeforeOpenSearch,
    needsToCloseOtherWindowBeforeOpeningSearch = true,
}) => {
    const [open, setOpen] = React.useState(false);
    const [defaultTab, setDefaultTab] = React.useState<GlobalSearchTab | undefined>(undefined);

    const openSearch = (initialTab?: GlobalSearchTab) => {
        setDefaultTab(initialTab);
        onBeforeOpenSearch();
        if (needsToCloseOtherWindowBeforeOpeningSearch) {
            setTimeout(() => {
                setOpen(true);
            }, 500);
        } else {
            setOpen(true);
        }
    };

    const { navigateToEntity } = useSearchEntityNavigation();

    React.useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
                if (
                    (e.target instanceof HTMLElement && e.target.isContentEditable) ||
                    e.target instanceof HTMLInputElement ||
                    e.target instanceof HTMLTextAreaElement ||
                    e.target instanceof HTMLSelectElement
                ) {
                    return;
                }

                e.preventDefault();
                setOpen(true);
            }
        };

        document.addEventListener('keydown', down);
        return () => document.removeEventListener('keydown', down);
    }, []);

    const runCommand = React.useCallback((command: () => unknown) => {
        command();
        setOpen(false);
    }, []);

    const handleItemSelected = React.useCallback(({ id, entity }: SearchEntityItem) => {
        runCommand(() => {
            return navigateToEntity({
                erp_id: id,
                entity: entity,
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <GlobalSearchContext.Provider value={{ openSearch, open }}>
            <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
                <div className="flex flex-col gap-3 w-xl">
                    <EntitySearchContent
                        entities={[
                            SearchEntity.Customer,
                            SearchEntity.Product,
                            SearchEntity.SalesOrder,
                            SearchEntity.SalesQuote,
                        ]}
                        onItemSelected={handleItemSelected}
                        initialTab={defaultTab}
                    />
                </div>
            </CommandDialog>
            {children}
        </GlobalSearchContext.Provider>
    );
};
